import React, { useState, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useMembershipPlanService from '../../app/services/hooks/useMembershipPlanService';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import InputField from '../../components/InputField';
import TextAreaField from '../../components/TextAreaField';
import SelectTwo from '../../components/SelectTwo';
import ButtonSaveForm from '../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../components/buttons/ButtonCancelForm';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';

interface FormData {
    name: string;
    code: string;
    plan_type: string;
    description: string;
    short_description: string;
    price: string;
    currency: string;
    duration_days: string;
    contracts_included: string;
    chatbot_enabled: boolean;
    chatbot_messages_limit: string;
    features: string[];
    exclusions: string[];
    is_featured: boolean;
    display_order: string;
    badge_text: string;
    is_active: boolean;
    // Planes de abogados
    audience: string;
    organization_type: string;
    max_seats: string;
    max_clients: string;
    max_brand_applications: string;
    white_label_level: string;
    can_publish_videos: boolean;
    support_level: string;
}

const AUDIENCE_OPTIONS = [
    { value: 'client', label: 'Clientes (contratos e IA)' },
    { value: 'lawyer', label: 'Abogados y estudios (SaaS)' }
];
const ORGANIZATION_TYPE_OPTIONS = [
    { value: 'independent', label: 'Abogado independiente (1 usuario)' },
    { value: 'firm', label: 'Estudio jurídico (varios usuarios)' }
];
const WHITE_LABEL_OPTIONS = [
    { value: 'none', label: 'Sin personalización' },
    { value: 'basic', label: 'Básico: logo, colores y nombre' },
    { value: 'full', label: 'Completo: además remitente y subdominio' }
];
const SUPPORT_OPTIONS = [
    { value: 'base', label: 'Base' },
    { value: 'standard', label: 'Estándar' },
    { value: 'priority', label: 'Prioritario' }
];

const MembershipPlanForm: React.FC = () => {
    const { id } = useParams<{ id?: string }>();
    const history = useHistory();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const {
        showMembershipPlan,
        updateMembershipPlan,
        storeMembershipPlan,
        fetchingShowPlan,
        fetchingUpdatePlan,
        fetchingStorePlan
    } = useMembershipPlanService();
    
    const isEditing = Boolean(id);
    
    const [formData, setFormData] = useState<FormData>({
        name: '',
        code: '',
        plan_type: 'premium',
        description: '',
        short_description: '',
        price: '39990',
        currency: 'CLP',
        duration_days: '30',
        contracts_included: '-1',
        chatbot_enabled: true,
        chatbot_messages_limit: '-1',
        features: [],
        exclusions: [],
        is_featured: true,
        display_order: '1',
        badge_text: '',
        is_active: true,
        audience: 'client',
        organization_type: '',
        max_seats: '1',
        max_clients: '-1',
        max_brand_applications: '-1',
        white_label_level: 'none',
        can_publish_videos: false,
        support_level: 'base'
    });
    
    const [loading, setLoading] = useState(false);
    const [newFeature, setNewFeature] = useState('');
    const [newExclusion, setNewExclusion] = useState('');
    
    useEffect(() => {
        if (id) {
            setLoading(true);
            showMembershipPlan(Number(id), {
                onSuccess: (res: any) => {
                    const plan = res.data.plan;
                    setFormData({
                        name: plan.name || '',
                        code: plan.code || '',
                        plan_type: plan.plan_type || 'premium',
                        description: plan.description || '',
                        short_description: plan.short_description || '',
                        price: plan.price ? plan.price.toString() : '0',
                        currency: plan.currency || 'CLP',
                        duration_days: plan.duration_days !== undefined ? plan.duration_days.toString() : '30',
                        contracts_included: plan.contracts_included !== undefined ? plan.contracts_included.toString() : '-1',
                        chatbot_enabled: plan.chatbot_enabled ?? true,
                        chatbot_messages_limit: plan.chatbot_messages_limit !== undefined ? plan.chatbot_messages_limit.toString() : '-1',
                        features: plan.features || [],
                        exclusions: plan.exclusions || [],
                        is_featured: plan.is_featured ?? false,
                        display_order: plan.display_order ? plan.display_order.toString() : '1',
                        badge_text: plan.badge_text || '',
                        is_active: plan.is_active ?? true,
                        audience: plan.audience || 'client',
                        organization_type: plan.organization_type || '',
                        max_seats: plan.max_seats !== undefined ? plan.max_seats.toString() : '1',
                        max_clients: plan.max_clients !== undefined ? plan.max_clients.toString() : '-1',
                        max_brand_applications: plan.max_brand_applications !== undefined ? plan.max_brand_applications.toString() : '-1',
                        white_label_level: plan.white_label_level || 'none',
                        can_publish_videos: plan.can_publish_videos ?? false,
                        support_level: plan.support_level || 'base'
                    });
                    setLoading(false);
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al cargar el plan');
                    setLoading(false);
                    history.push('/intranet/membership-plans');
                }
            });
        }
    }, [id]);
    
    const handleInputChange = (field: keyof FormData) => (value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleAddFeature = () => {
        if (newFeature.trim()) {
            setFormData(prev => ({
                ...prev,
                features: [...prev.features, newFeature.trim()]
            }));
            setNewFeature('');
        }
    };

    const handleRemoveFeature = (index: number) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const handleAddExclusion = () => {
        if (newExclusion.trim()) {
            setFormData(prev => ({
                ...prev,
                exclusions: [...prev.exclusions, newExclusion.trim()]
            }));
            setNewExclusion('');
        }
    };

    const handleRemoveExclusion = (index: number) => {
        setFormData(prev => ({
            ...prev,
            exclusions: prev.exclusions.filter((_, i) => i !== index)
        }));
    };
    
    const handleSubmit = () => {
        const submitData = {
            ...formData,
            price: Number(formData.price),
            duration_days: Number(formData.duration_days),
            contracts_included: Number(formData.contracts_included),
            chatbot_messages_limit: Number(formData.chatbot_messages_limit),
            display_order: Number(formData.display_order),
            max_seats: Number(formData.max_seats),
            max_clients: Number(formData.max_clients),
            max_brand_applications: Number(formData.max_brand_applications)
        };
        
        if (isEditing) {
            updateMembershipPlan(Number(id), submitData, {
                onSuccess: (res: any) => {
                    toast.success(res.message || 'Plan actualizado exitosamente');
                    setErrors({});
                    history.push('/intranet/membership-plans');
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al actualizar el plan');
                },
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Por favor corrige los errores en el formulario');
                }
            });
        } else {
            storeMembershipPlan(submitData, {
                onSuccess: (res: any) => {
                    toast.success(res.message || 'Plan creado exitosamente');
                    setErrors({});
                    history.push('/intranet/membership-plans');
                },
                onError: (res: any) => {
                    toast.error(res.message || 'Error al crear el plan');
                },
                onFieldError: (res: any) => {
                    setErrors(res.data);
                    toast.error('Por favor corrige los errores en el formulario');
                }
            });
        }
    };
    
    const handleCancel = () => {
        history.push('/intranet/membership-plans');
    };
    
    const planTypeOptions = [
        { value: 'free', label: 'Gratuito' },
        { value: 'basic', label: 'Básico' },
        { value: 'premium', label: 'Premium' }
    ];
    
    const currencyOptions = [
        { value: 'CLP', label: 'CLP - Peso Chileno' }
    ];

    const contractsOptions = [
        { value: '-1', label: 'Ilimitados' },
        { value: '0', label: 'No incluye' },
        { value: '1', label: '1 contrato' },
        { value: '3', label: '3 contratos' },
        { value: '5', label: '5 contratos' },
        { value: '10', label: '10 contratos' },
        { value: '20', label: '20 contratos' }
    ];

    const chatbotOptions = [
        { value: '-1', label: 'Ilimitado' },
        { value: '0', label: 'No incluye' },
        { value: '10', label: '10 mensajes' },
        { value: '25', label: '25 mensajes' },
        { value: '50', label: '50 mensajes' },
        { value: '100', label: '100 mensajes' }
    ];

    const durationOptions = [
        { value: '0', label: 'De por vida' },
        { value: '7', label: '7 días' },
        { value: '30', label: '1 mes (30 días)' },
        { value: '90', label: '3 meses (90 días)' },
        { value: '180', label: '6 meses (180 días)' },
        { value: '365', label: '1 año (365 días)' }
    ];
    
    const isSaving = fetchingUpdatePlan || fetchingStorePlan;
    
    if (loading || fetchingShowPlan) {
        return (
            <div className="container-fluid">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    // Calcular precio con IVA para vista previa
    const priceNum = Number(formData.price) || 0;
    const iva = priceNum * 0.19;
    const totalWithIva = priceNum + iva;

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', path: '#' },
                    { label: 'Planes de Membresía', path: '/intranet/membership-plans' },
                    { label: isEditing ? 'Editar' : 'Crear', isCurrent: true }
                ]}
                title={isEditing ? "Editar Plan de Membresía" : "Crear Plan de Membresía"}
                description={isEditing 
                    ? "Actualizar información del plan" 
                    : "Configurar un nuevo plan de membresía"
                }
            />
            
            <DefaultCard>
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="row">
                        {/* Nombre del Plan */}
                        <div className="col-md-6 mb-3">
                            <InputField
                                label="Nombre del Plan"
                                value={formData.name}
                                onChange={handleInputChange('name')}
                                onFocus={() => onFocusRemove('name')}
                                error={fieldErrorMessage('name')}
                                placeholder="Ej: Plan Monere Contracts + AI"
                                required
                            />
                        </div>
                        
                        {/* Código único */}
                        <div className="col-md-3 mb-3">
                            <InputField
                                label="Código"
                                value={formData.code}
                                onChange={handleInputChange('code')}
                                onFocus={() => onFocusRemove('code')}
                                error={fieldErrorMessage('code')}
                                placeholder="Ej: monere_contracts_ai"
                                required
                            />
                            <small className="text-muted">Identificador único (sin espacios)</small>
                        </div>

                        {/* Tipo de plan */}
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Tipo de Plan</label>
                            <SelectTwo
                                inputValue={formData.plan_type}
                                onChange={(option) =>
                                    setFormData(prev => ({ ...prev, plan_type: option?.value || 'premium' }))
                                }
                                options={planTypeOptions}
                                isClearable={false}
                            />
                        </div>
                        
                        {/* Precio */}
                        <div className="col-md-3 mb-3">
                            <InputField
                                label="Precio (neto sin IVA)"
                                type="number"
                                value={formData.price}
                                onChange={handleInputChange('price')}
                                onFocus={() => onFocusRemove('price')}
                                error={fieldErrorMessage('price')}
                                placeholder="39990"
                                required
                            />
                            <small className="text-muted">
                                Con IVA: ${totalWithIva.toLocaleString('es-CL')}
                            </small>
                        </div>
                        
                        {/* Moneda */}
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Moneda</label>
                            <SelectTwo
                                inputValue={formData.currency}
                                onChange={(option) =>
                                    setFormData(prev => ({ ...prev, currency: option?.value || 'CLP' }))
                                }
                                options={currencyOptions}
                                isClearable={false}
                            />
                        </div>

                        {/* Duración */}
                        <div className="col-md-3 mb-3">
                            <label className="form-label">Duración</label>
                            <SelectTwo
                                inputValue={formData.duration_days}
                                onChange={(option) =>
                                    setFormData(prev => ({ ...prev, duration_days: option?.value || '30' }))
                                }
                                options={durationOptions}
                                isClearable={false}
                            />
                        </div>

                        {/* Badge */}
                        <div className="col-md-3 mb-3">
                            <InputField
                                label="Etiqueta (Badge)"
                                value={formData.badge_text}
                                onChange={handleInputChange('badge_text')}
                                placeholder="Ej: Oferta de lanzamiento"
                            />
                        </div>

                        {/* Contratos incluidos */}
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Contratos Incluidos</label>
                            <SelectTwo
                                inputValue={formData.contracts_included}
                                onChange={(option) =>
                                    setFormData(prev => ({ ...prev, contracts_included: option?.value || '-1' }))
                                }
                                options={contractsOptions}
                                isClearable={false}
                            />
                            <small className="text-muted">-1 = Ilimitados</small>
                        </div>

                        {/* Chatbot habilitado */}
                        <div className="col-md-4 mb-3">
                            <label className="form-label">Chatbot Legal</label>
                            <SelectTwo
                                inputValue={formData.chatbot_messages_limit}
                                onChange={(option) => {
                                    const value = option?.value || '-1';
                                    setFormData(prev => ({ 
                                        ...prev, 
                                        chatbot_messages_limit: value,
                                        chatbot_enabled: value !== '0'
                                    }));
                                }}
                                options={chatbotOptions}
                                isClearable={false}
                            />
                            <small className="text-muted">-1 = Ilimitado</small>
                        </div>

                        {/* Checkboxes */}
                        <div className="col-md-4 mb-3 d-flex align-items-center gap-4">
                            <div className="form-check mt-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="isFeatured"
                                    checked={formData.is_featured}
                                    onChange={(e) => handleInputChange('is_featured')(e.target.checked)}
                                    style={{ width: '1.3rem', height: '1.3rem', cursor: 'pointer' }}
                                />
                                <label className="form-check-label ms-2" htmlFor="isFeatured" style={{ cursor: 'pointer' }}>
                                    Destacado
                                </label>
                            </div>
                            <div className="form-check mt-4">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="isActive"
                                    checked={formData.is_active}
                                    onChange={(e) => handleInputChange('is_active')(e.target.checked)}
                                    style={{ width: '1.3rem', height: '1.3rem', cursor: 'pointer' }}
                                />
                                <label className="form-check-label ms-2" htmlFor="isActive" style={{ cursor: 'pointer' }}>
                                    Activo
                                </label>
                            </div>
                        </div>

                        {/* Audiencia y límites de planes de abogados */}
                        <div className="col-md-6 mb-3">
                            <SelectTwo
                                label="Dirigido a"
                                inputValue={formData.audience}
                                options={AUDIENCE_OPTIONS}
                                onChange={(option: any) => setFormData(prev => ({ ...prev, audience: option?.value || 'client' }))}
                                hasError={!!fieldErrorMessage('audience')}
                            />
                        </div>
                        {formData.audience === 'lawyer' && (
                            <>
                                <div className="col-md-6 mb-3">
                                    <SelectTwo
                                        label="Tipo de cuenta"
                                        inputValue={formData.organization_type}
                                        options={ORGANIZATION_TYPE_OPTIONS}
                                        onChange={(option: any) => setFormData(prev => ({ ...prev, organization_type: option?.value || '' }))}
                                        hasError={!!fieldErrorMessage('organization_type')}
                                    />
                                    {fieldErrorMessage('organization_type') && <small className="text-danger">{fieldErrorMessage('organization_type')}</small>}
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField label="Usuarios (-1 ilimitados)" type="number" value={formData.max_seats}
                                                disabled={formData.organization_type === 'independent'}
                                                onChange={handleInputChange('max_seats')} onFocus={() => onFocusRemove('max_seats')}
                                                error={fieldErrorMessage('max_seats')} />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField label="Clientes en cartera (-1 ilimitados)" type="number" value={formData.max_clients}
                                                onChange={handleInputChange('max_clients')} onFocus={() => onFocusRemove('max_clients')}
                                                error={fieldErrorMessage('max_clients')} />
                                </div>
                                <div className="col-md-4 mb-3">
                                    <InputField label="Marcas en curso (-1 ilimitadas)" type="number" value={formData.max_brand_applications}
                                                onChange={handleInputChange('max_brand_applications')} onFocus={() => onFocusRemove('max_brand_applications')}
                                                error={fieldErrorMessage('max_brand_applications')} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <SelectTwo label="Marca blanca" inputValue={formData.white_label_level} options={WHITE_LABEL_OPTIONS}
                                               onChange={(option: any) => setFormData(prev => ({ ...prev, white_label_level: option?.value || 'none' }))} />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <SelectTwo label="Soporte" inputValue={formData.support_level} options={SUPPORT_OPTIONS}
                                               onChange={(option: any) => setFormData(prev => ({ ...prev, support_level: option?.value || 'base' }))} />
                                </div>
                            </>
                        )}

                        {/* Descripción corta */}
                        <div className="col-md-12 mb-3">
                            <InputField
                                label="Descripción Corta"
                                value={formData.short_description}
                                onChange={handleInputChange('short_description')}
                                onFocus={() => onFocusRemove('short_description')}
                                error={fieldErrorMessage('short_description')}
                                placeholder="Ej: Acceso legal continuo, simple y sin sorpresas"
                            />
                        </div>
                        
                        {/* Descripción completa */}
                        <div className="col-md-12 mb-3">
                            <TextAreaField
                                label="Descripción Completa"
                                value={formData.description}
                                onChange={handleInputChange('description')}
                                onFocus={() => onFocusRemove('description')}
                                error={fieldErrorMessage('description')}
                                rows={3}
                                placeholder="Describe qué incluye este plan..."
                            />
                        </div>

                        {/* Características */}
                        <div className="col-md-12 mt-3 mb-4">
                            <div className="p-4 rounded-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <h6 className="mb-2" style={{ color: '#001849', fontWeight: 600 }}>
                                    <i className="fas fa-check-circle me-2 text-success"></i>
                                    Características del Plan
                                </h6>
                            
                                <div className="d-flex gap-2 mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newFeature}
                                        onChange={(e) => setNewFeature(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddFeature())}
                                        placeholder="Ej: Contratos automatizados ilimitados"
                                        style={{ borderRadius: '8px', padding: '0.75rem 1rem' }}
                                    />
                                    <ButtonGeneral
                                        onClick={handleAddFeature}
                                        title="Agregar"
                                        variant="primary"
                                        size="small"
                                        icon="fa-plus"
                                        iconPosition="left"
                                        disabled={!newFeature.trim()}
                                    />
                                </div>

                                {formData.features.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2 p-3 rounded" style={{ background: '#e6f0ff' }}>
                                        {formData.features.map((feature, index) => (
                                            <div
                                                key={index}
                                                className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                                                style={{ background: 'white', border: '1px solid #001849' }}
                                            >
                                                <i className="fas fa-check text-success"></i>
                                                <span>{feature}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveFeature(index)}
                                                    className="btn btn-link p-0 text-danger"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-3 text-muted">
                                        No hay características agregadas
                                    </div>
                                )}

                                {/* Sugerencias */}
                                <div className="mt-3">
                                    <small className="text-muted d-block mb-2">
                                        <i className="fas fa-lightbulb me-1 text-warning"></i>
                                        Sugerencias:
                                    </small>
                                    <div className="d-flex flex-wrap gap-1">
                                        {[
                                            'Contratos automatizados ilimitados',
                                            'NDA (acuerdos de confidencialidad)',
                                            'Contratos de prestación de servicios',
                                            'Contratos de trabajo indefinido',
                                            'Adaptados a normativa chilena',
                                            'Chatbot Legal con IA 24/7',
                                            'Resolver dudas legales rápidas'
                                        ].filter(s => !formData.features.includes(s)).map((suggestion, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                className="btn btn-sm"
                                                onClick={() => setFormData(prev => ({ ...prev, features: [...prev.features, suggestion] }))}
                                                style={{ background: 'white', border: '1px solid #dee2e6', borderRadius: '20px', fontSize: '0.8rem' }}
                                            >
                                                <i className="fas fa-plus me-1"></i>
                                                {suggestion}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Exclusiones */}
                        <div className="col-md-12 mb-4">
                            <div className="p-4 rounded-3" style={{ background: '#fef3f3', border: '1px solid #f5c2c2' }}>
                                <h6 className="mb-2" style={{ color: '#721c24', fontWeight: 600 }}>
                                    <i className="fas fa-times-circle me-2 text-danger"></i>
                                    Lo que NO incluye
                                </h6>
                            
                                <div className="d-flex gap-2 mb-3">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={newExclusion}
                                        onChange={(e) => setNewExclusion(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExclusion())}
                                        placeholder="Ej: Registro de marcas"
                                        style={{ borderRadius: '8px', padding: '0.75rem 1rem' }}
                                    />
                                    <ButtonGeneral
                                        onClick={handleAddExclusion}
                                        title="Agregar"
                                        variant="danger"
                                        size="small"
                                        icon="fa-plus"
                                        iconPosition="left"
                                        disabled={!newExclusion.trim()}
                                    />
                                </div>

                                {formData.exclusions.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2 p-3 rounded" style={{ background: '#fff5f5' }}>
                                        {formData.exclusions.map((exclusion, index) => (
                                            <div
                                                key={index}
                                                className="d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                                                style={{ background: 'white', border: '1px solid #dc3545', color: '#721c24' }}
                                            >
                                                <i className="fas fa-times text-danger"></i>
                                                <span>{exclusion}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveExclusion(index)}
                                                    className="btn btn-link p-0"
                                                    style={{ color: '#721c24' }}
                                                >
                                                    <i className="fas fa-times-circle"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-3 text-muted">
                                        No hay exclusiones agregadas
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Vista previa */}
                    {formData.name && priceNum >= 0 && (
                        <div 
                            className="p-4 rounded-3 mb-4"
                            style={{
                                background: 'linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%)',
                                border: '2px solid #001849'
                            }}
                        >
                            <h6 className="mb-3" style={{ color: '#001849' }}>
                                <i className="fas fa-eye me-2"></i>
                                Vista Previa del Plan
                            </h6>
                            <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                    {formData.badge_text && (
                                        <span className="badge bg-info mb-2">{formData.badge_text}</span>
                                    )}
                                    <h5 className="mb-1" style={{ color: '#001849', fontWeight: 600 }}>
                                        {formData.name}
                                        {formData.is_featured && (
                                            <span className="badge bg-warning ms-2" style={{ fontSize: '0.65rem' }}>Destacado</span>
                                        )}
                                    </h5>
                                    {formData.short_description && (
                                        <p className="text-muted mb-2">{formData.short_description}</p>
                                    )}
                                    <div className="d-flex gap-3 mb-2">
                                        <span className="badge bg-secondary">
                                            {durationOptions.find(d => d.value === formData.duration_days)?.label}
                                        </span>
                                        <span className={`badge ${formData.contracts_included === '-1' ? 'bg-success' : 'bg-primary'}`}>
                                            Contratos: {contractsOptions.find(c => c.value === formData.contracts_included)?.label}
                                        </span>
                                        <span className={`badge ${formData.chatbot_messages_limit !== '0' ? 'bg-success' : 'bg-secondary'}`}>
                                            Chatbot: {chatbotOptions.find(c => c.value === formData.chatbot_messages_limit)?.label}
                                        </span>
                                    </div>
                                    {formData.features.length > 0 && (
                                        <ul className="list-unstyled mb-0 mt-3">
                                            {formData.features.slice(0, 5).map((feature, idx) => (
                                                <li key={idx} className="mb-1" style={{ fontSize: '0.85rem' }}>
                                                    <i className="fas fa-check text-success me-2"></i>
                                                    {feature}
                                                </li>
                                            ))}
                                            {formData.features.length > 5 && (
                                                <li className="text-muted" style={{ fontSize: '0.85rem' }}>
                                                    ...y {formData.features.length - 5} más
                                                </li>
                                            )}
                                        </ul>
                                    )}
                                </div>
                                <div className="text-end ms-4">
                                    <span style={{ fontSize: '1.75rem', fontWeight: 700, color: '#001849' }}>
                                        ${priceNum.toLocaleString('es-CL')}
                                    </span>
                                    <span className="text-muted d-block" style={{ fontSize: '0.85rem' }}>
                                        + IVA = ${totalWithIva.toLocaleString('es-CL')}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Botones */}
                    <div className="d-flex gap-2 justify-content-end mt-4">
                        <ButtonCancelForm callbackCancel={handleCancel} />
                        <ButtonSaveForm
                            callbackSave={handleSubmit}
                            loading={isSaving}
                            title={isEditing ? "Actualizar" : "Crear Plan"}
                        />
                    </div>
                </form>
            </DefaultCard>
        </div>
    );
};

export default MembershipPlanForm;
